import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, fmtDate } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CheckCircle, XCircle, Plus, Trash, PencilSimple, Package, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import MaterialAutocomplete from "@/components/MaterialAutocomplete";

const UNITS = ["nos", "mtr", "kg", "ltr", "box", "roll", "set", "cyl", "pkt"];
const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

/**
 * ReviewMRDialog — the admin's review-and-approve flow. Opens on the Approve
 * click. Admin can EDIT every line item (description, sku, unit, qty, notes)
 * plus priority, required_by, notes — then approve, reject, or just save.
 * Also usable by managers/requesters as a read-only detail view.
 */
export default function ReviewMRDialog({ mr, projects, currentUser, onClose, onDone }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);

  const isAdmin = currentUser?.role === "admin";
  const isSubmitted = mr.status === "submitted";
  const canEdit = (isSubmitted && isAdmin) || (mr.status === "draft" && (isAdmin || mr.requested_by === currentUser?.id));
  const canApprove = isSubmitted && isAdmin;

  useEffect(() => {
    setForm({
      priority: mr.priority,
      required_by: mr.required_by || "",
      notes: mr.notes || "",
      items: (mr.items || []).map((it) => ({
        description: it.description || "",
        sku: it.sku || "",
        unit: it.unit || "nos",
        quantity: Number(it.quantity || 1),
        notes: it.notes || "",
      })),
    });
  }, [mr]);

  const projectName = projects.find((p) => p.id === mr.project_id)?.name || "—";

  const updateLine = (i, key, value) =>
    setForm((f) => ({ ...f, items: f.items.map((row, idx) => (idx === i ? { ...row, [key]: value } : row)) }));
  const addLine = () =>
    setForm((f) => ({ ...f, items: [...f.items, { description: "", sku: "", unit: "nos", quantity: 1, notes: "" }] }));
  const removeLine = (i) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const saveChanges = async () => {
    // Persists edits without changing status
    const clean = form.items
      .filter((it) => (it.description || "").trim())
      .map((it) => ({
        description: it.description.trim(),
        sku: (it.sku || "").trim(),
        unit: it.unit || "nos",
        quantity: Math.max(Number(it.quantity) || 0, 0),
        notes: it.notes || "",
      }))
      .filter((it) => it.quantity > 0);
    if (!clean.length) {
      toast.error("At least one item with quantity > 0 is required");
      return false;
    }
    await api.put(`/material-requests/${mr.id}`, {
      project_id: mr.project_id,
      priority: form.priority,
      required_by: form.required_by || null,
      notes: form.notes,
      items: clean,
    });
    return true;
  };

  const onSaveOnly = async () => {
    if (!canEdit) return onClose();
    setBusy(true);
    try {
      if (await saveChanges()) {
        toast.success(`${mr.mr_no} updated`);
        onDone?.();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const onApprove = async () => {
    setBusy(true);
    try {
      // Save any edits first (if applicable)
      if (canEdit) {
        if (!(await saveChanges())) { setBusy(false); return; }
      }
      await api.post(`/material-requests/${mr.id}/approve`);
      toast.success(`${mr.mr_no} approved · ready for fulfilment`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const onReject = async () => {
    if (!rejectReason.trim()) return toast.error("Please add a rejection reason");
    setBusy(true);
    try {
      await api.post(`/material-requests/${mr.id}/reject`, { reason: rejectReason });
      toast.success(`${mr.mr_no} rejected`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  if (!form) return null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package size={16} weight="fill" className="text-[#EA580C]" />
            Review {mr.mr_no}
            <span className="text-[11px] font-semibold uppercase tracking-widest bg-slate-100 px-1.5 py-[1px] rounded-sm text-slate-600 ml-1">{mr.status}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Meta row */}
        <div className="grid grid-cols-4 gap-3 text-[12px] pt-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">Project</div>
            <div className="font-medium text-slate-800">{projectName}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">Requested by</div>
            <div className="font-medium text-slate-800">{mr.requested_by_name}</div>
            <div className="text-[10.5px] text-slate-500">{fmtDate(mr.created_at)}</div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5 block">Priority</Label>
            {canEdit ? (
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="h-7 text-[12px]" data-testid="review-mr-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : <span className="font-medium text-slate-800 uppercase">{mr.priority}</span>}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5 block">Required by</Label>
            {canEdit ? (
              <Input type="date" value={form.required_by} onChange={(e) => setForm({ ...form, required_by: e.target.value })} className="h-7 text-[12px]" data-testid="review-mr-required-by" />
            ) : <span className="font-medium text-slate-800">{mr.required_by ? fmtDate(mr.required_by) : "—"}</span>}
          </div>
        </div>

        {/* Items table */}
        <div className="mt-3 border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">Items · {form.items.length}</div>
            {canEdit && (
              <button onClick={addLine} data-testid="review-add-line" className="text-[11px] font-semibold text-[#EA580C] hover:underline flex items-center gap-1">
                <Plus size={12} /> Add row
              </button>
            )}
          </div>
          <Table className="bc-list text-[12.5px]">
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                <TableHead className="w-32">SKU</TableHead>
                <TableHead className="w-20">Unit</TableHead>
                <TableHead className="w-20 text-right">Qty</TableHead>
                <TableHead>Notes</TableHead>
                {canEdit && <TableHead className="w-8" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {form.items.map((it, i) => (
                <TableRow key={`review-line-${i}`} data-testid={`review-line-${i}`}>
                  <TableCell>
                    {canEdit ? (
                      <MaterialAutocomplete
                        value={it.description}
                        onChange={(v) => updateLine(i, "description", v)}
                        onPick={(m) => {
                          setForm((f) => ({
                            ...f,
                            items: f.items.map((row, idx) =>
                              idx === i
                                ? { ...row, description: m.description, sku: m.sku || row.sku, unit: m.unit || row.unit }
                                : row,
                            ),
                          }));
                        }}
                        testid={`review-line-desc-${i}`}
                      />
                    ) : <span className="text-slate-800">{it.description}</span>}
                  </TableCell>
                  <TableCell>
                    {canEdit ? (
                      <Input value={it.sku} onChange={(e) => updateLine(i, "sku", e.target.value)} className="h-7 font-mono text-[11.5px]" />
                    ) : <span className="font-mono text-[11.5px] text-slate-600">{it.sku || "—"}</span>}
                  </TableCell>
                  <TableCell>
                    {canEdit ? (
                      <Select value={it.unit} onValueChange={(v) => updateLine(i, "unit", v)}>
                        <SelectTrigger className="h-7"><SelectValue /></SelectTrigger>
                        <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : <span className="text-slate-700">{it.unit}</span>}
                  </TableCell>
                  <TableCell>
                    {canEdit ? (
                      <Input type="number" value={it.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} className="h-7 text-right font-mono-num" data-testid={`review-line-qty-${i}`} />
                    ) : <span className="font-mono-num text-slate-800">{it.quantity}</span>}
                  </TableCell>
                  <TableCell>
                    {canEdit ? (
                      <Input value={it.notes} onChange={(e) => updateLine(i, "notes", e.target.value)} className="h-7 text-[12px]" />
                    ) : <span className="text-[12px] text-slate-600">{it.notes || "—"}</span>}
                  </TableCell>
                  {canEdit && (
                    <TableCell>
                      <button onClick={() => removeLine(i)} className="text-slate-400 hover:text-red-600"><Trash size={12} /></button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Manager notes / justification */}
        <div className="mt-3">
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block">Justification / Notes from requester</Label>
          {canEdit ? (
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="review-notes" />
          ) : (
            <div className="text-[12.5px] text-slate-700 bg-slate-50 border border-slate-200 rounded-sm p-2 min-h-[40px] whitespace-pre-wrap">{mr.notes || <span className="italic text-slate-400">No notes</span>}</div>
          )}
        </div>

        {/* Reject panel */}
        {showReject && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-sm p-3">
            <Label className="text-[10.5px] font-semibold uppercase tracking-widest text-red-700 mb-1 block">Reject reason</Label>
            <Textarea rows={2} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="review-reject-reason" placeholder="Why is this being rejected?" />
            <div className="flex gap-2 mt-2">
              <Button variant="outline" onClick={() => { setShowReject(false); setRejectReason(""); }} className="rounded-sm">Cancel</Button>
              <Button onClick={onReject} disabled={busy || !rejectReason.trim()} data-testid="review-confirm-reject" className="bg-red-600 hover:bg-red-700 text-white rounded-sm flex-1">
                <XCircle size={14} className="mr-1.5" /> Confirm reject
              </Button>
            </div>
          </div>
        )}

        {/* Action bar */}
        {!showReject && (
          <div className="flex items-center gap-2 pt-3 border-t border-slate-200 mt-3">
            <Button variant="outline" onClick={onClose} className="rounded-sm">Close</Button>
            {canEdit && (
              <Button variant="outline" onClick={onSaveOnly} disabled={busy} data-testid="review-save-only" className="rounded-sm border-slate-300 hover:border-[#EA580C]">
                <FloppyDisk size={14} className="mr-1.5" /> Save changes
              </Button>
            )}
            {canApprove && (
              <>
                <Button onClick={() => setShowReject(true)} disabled={busy} data-testid="review-open-reject" variant="outline" className="rounded-sm border-red-300 text-red-700 hover:bg-red-50 hover:border-red-500">
                  <XCircle size={14} className="mr-1.5" /> Reject…
                </Button>
                <Button onClick={onApprove} disabled={busy} data-testid="review-approve" className="flex-1 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white">
                  <CheckCircle size={14} weight="fill" className="mr-1.5" />
                  {busy ? "Saving & approving…" : canEdit ? "Save changes & Approve" : "Approve"}
                </Button>
              </>
            )}
            {!canApprove && !canEdit && (
              <span className="ml-auto text-[11px] text-slate-500 italic">Read-only view</span>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
